import { useCallback, useEffect, useRef, useState } from "react";
import { listCommsSiteNotes } from "../../services/comms/comms.service";
import { lookupUsers } from "../../services/userManagement/userManagement.service";
import { getCommsErrorMessage } from "./commsSite";
import { normalizeLookupId, parseSiteNotes, sortNotesNewestFirst } from "./commsNotes";
import type { SiteNote } from "./commsNotes";

export interface NoteHistory {
    notes: SiteNote[];
    loading: boolean;
    error: string | null;
}
const EMPTY_HISTORY: NoteHistory = { notes: [], loading: true, error: null };

export function useCommsNotes(revision: number) {
    const [histories, setHistories] = useState<Record<number, NoteHistory>>({});
    const [users, setUsers] = useState<Record<string, string>>({});
    const [userError, setUserError] = useState<string | null>(null);
    const cached = useRef(new Map<number, Promise<NoteHistory>>());
    const mounted = useRef(false);
    const generation = useRef(0);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; generation.current += 1; };
    }, []);
    useEffect(() => {
        generation.current += 1;
        cached.current.clear();
        setHistories({});
    }, [revision]);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const response = await lookupUsers();
                if (!response.success || !response.data) throw new Error(response.error?.message || "Failed to load message authors");
                const lookup: Record<string, string> = {};
                response.data.forEach(user => {
                    const fullName = [user.firstName, user.lastName].filter(value => value?.trim()).join(" ").trim();
                    const name = user.displayName?.trim() || fullName;
                    if (user.id?.trim() && name) lookup[normalizeLookupId(user.id)] = name;
                });
                if (!cancelled) {
                    setUsers(lookup);
                    setUserError(null);
                }
            } catch (error: unknown) {
                if (!cancelled) {
                    setUserError(`Some message authors are unavailable: ${getCommsErrorMessage(error, "Failed to load message authors")}`);
                }
            }
        };
        void load();
        return () => { cancelled = true; };
    }, []);

    const load = useCallback((id: number, force = false): Promise<NoteHistory> => {
        const existing = cached.current.get(id);
        if (existing && !force) return existing;
        const current = generation.current;
        setHistories(previous => ({ ...previous, [id]: { notes: previous[id]?.notes ?? [], loading: true, error: null } }));
        const request = (async (): Promise<NoteHistory> => {
            try {
                const response = await listCommsSiteNotes(id);
                if (!response.success) throw new Error(response.error?.message || "Failed to load site notes");
                return { notes: sortNotesNewestFirst(parseSiteNotes(response.data)), loading: false, error: null };
            } catch (error: unknown) {
                return { notes: [], loading: false, error: getCommsErrorMessage(error, "Failed to load site notes") };
            }
        })();
        cached.current.set(id, request);
        void request.then(history => {
            if (mounted.current && current === generation.current && cached.current.get(id) === request) {
                setHistories(previous => ({ ...previous, [id]: history }));
            }
        });
        return request;
    }, [revision]);

    return { histories, users, userError, load, emptyHistory: EMPTY_HISTORY };
}
